import { useEffect, useMemo, useRef, useState } from "react";
import { toast, Toaster } from "sonner";
import { AlertCircle, CheckCircle, Share2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import { BankTransferInstructions, TransferAccountDetails } from "./BankTransferInstructions";
import { resolveRegionFromCurrency, RegionCode } from "../utils/regions";
import { apiClient } from "../utils/apiClient";
import { useMockApi } from "../utils/config";
import { useCopyFeedbackContext } from "./CopyFeedbackProvider";

interface PublicPayLink {
  slug: string;
  title?: string | null;
  message?: string | null;
  amount: number;
  currency: string;
  status: string;
  expiresAt?: string | null;
  recipientName?: string | null;
  owner?: { name?: string | null; avatar?: string | null };
  bankTransferInstructions?: TransferAccountDetails | null;
}

interface PayLinkPublicPageProps {
  token: string;
}

function resolveApiBase() {
  const rawPublicBase = (import.meta.env.VITE_PUBLIC_API_BASE_URL as string | undefined)?.trim();
  if (rawPublicBase) {
    return rawPublicBase.replace(/\/+$/, "");
  }

  const rawApiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim();
  if (rawApiBase && /^https?:\/\//i.test(rawApiBase)) {
    try {
      const parsed = new URL(rawApiBase);
      let path = parsed.pathname.replace(/\/+$/, "");
      if (path.toLowerCase().endsWith("/api")) {
        path = path.slice(0, -4);
      }
      return `${parsed.origin}${path}`;
    } catch {
      // fall through to defaults
    }
  }

  if (import.meta.env.DEV) {
    return "http://localhost:5000";
  }

  return "";
}

const PAY_LINK_API_BASE = resolveApiBase();
const buildPayLinkApiUrl = (path: string) => {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return PAY_LINK_API_BASE ? `${PAY_LINK_API_BASE}${normalized}` : normalized;
};

const BankdropGlyph = () => (
  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-br from-primary to-emerald-500 text-[11px] font-black uppercase tracking-tight text-primary-foreground shadow-sm">
    <span className="sr-only">Bankdrop</span>
    <span aria-hidden="true">B</span>
  </span>
);

export function PayLinkPublicPage({ token }: PayLinkPublicPageProps) {
  const [payLink, setPayLink] = useState<PublicPayLink | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<"pending" | "sent" | "confirmed">("pending");
  const [marking, setMarking] = useState(false);
  const [showFloatingInvite, setShowFloatingInvite] = useState(false);
  const lastScrollYRef = useRef(0);
  const mockMode = useMockApi;
  const { feedback: copyFeedback, triggerFeedback: triggerCopyFeedback } =
    useCopyFeedbackContext();

  useEffect(() => {
    let cancelled = false;
    const fetchPayLink = async () => {
      setLoading(true);
      setError(null);
      try {
        let payload: PublicPayLink | null = null;
        if (mockMode) {
          const data = await apiClient(`/pay-links/${token}`);
          payload = (data?.payLink ?? data) as PublicPayLink | null;
        } else {
          const res = await fetch(buildPayLinkApiUrl(`/pay-links/${token}`));
          const data = await res.json().catch(() => null);
          if (!res.ok) {
            const message =
              data?.error ||
              (res.status === 410
                ? "This pay link has expired or is no longer available."
                : "We couldn't find this pay link.");
            throw new Error(message);
          }
          payload = (data?.payLink ?? data) as PublicPayLink | null;
        }
        if (cancelled) return;
        if (!payload) {
          throw new Error("Invalid response from server.");
        }
        setPayLink(payload);
        setStatus(payload.status === "FULFILLED" ? "sent" : "pending");
      } catch (err) {
        if (cancelled) return;
        setPayLink(null);
        setStatus("pending");
        setError(
          err instanceof Error ? err.message : "Failed to load payment link",
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchPayLink();
    return () => {
      cancelled = true;
    };
  }, [token, mockMode]);

  const region: RegionCode | undefined = useMemo(() => {
    return resolveRegionFromCurrency(payLink?.currency);
  }, [payLink?.currency]);

  const referenceDisplay = useMemo(() => {
    if (!payLink) return undefined;
    const suffix = payLink.slug ? payLink.slug.toUpperCase() : token;
    return `PAYLINK-${suffix}`;
  }, [payLink, token]);

  const recipientName =
    payLink?.owner?.name || payLink?.recipientName || "Your contact";
  const inviteeName = payLink?.owner?.name || "Bankdrop";
  const handleJoinClick = () =>
    window.open("https://bankdrop.io", "_blank", "noopener");
  const joinButtonClasses =
    "bg-gradient-to-r from-primary via-emerald-500 to-primary/80 text-primary-foreground font-semibold px-6 py-3 rounded-full shadow-lg hover:shadow-xl hover:scale-[1.01] transition-all border border-white/10";
  const handleSharePayLink = async () => {
    if (typeof window === "undefined") return;
    const shareUrl = window.location.href;
    const shareTitle =
      payLink?.title || `Send a transfer${recipientName ? ` to ${recipientName}` : ""}`;
    const defaultText =
      payLink?.message ||
      `Scan and pay ${recipientName || "this contact"} securely on Bankdrop.`;
    try {
      if (navigator?.share) {
        await navigator.share({
          title: shareTitle,
          text: defaultText,
          url: shareUrl,
        });
        return;
      }
    } catch (err) {
      if ((err as DOMException)?.name === "AbortError") {
        return;
      }
      console.error("Share failed", err);
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        const message = "Pay link copied to clipboard";
        toast.success(message);
        triggerCopyFeedback("success", message);
        return;
      }
    } catch (clipboardError) {
      console.error("Share fallback failed", clipboardError);
      triggerCopyFeedback(
        "error",
        "Unable to copy pay link. Please copy manually.",
      );
    }
    toast.info("Copy this link to share:", {
      description: shareUrl,
    });
  };

  const handleMarkPaid = async () => {
    setMarking(true);
    try {
      let payload: PublicPayLink | null = null;
      if (mockMode) {
        const data = await apiClient(`/pay-links/${token}/mark-paid`, {
          method: "POST",
        });
        payload = (data?.payLink ?? data) as PublicPayLink | null;
      } else {
        const res = await fetch(buildPayLinkApiUrl(`/pay-links/${token}/mark-paid`), {
          method: "POST",
        });
        const data = await res.json().catch(() => null);
        if (!res.ok) {
          throw new Error(
            data?.error || "We couldn't confirm this payment just yet.",
          );
        }
        payload = (data?.payLink ?? data) as PublicPayLink | null;
      }
      if (!payload) {
        throw new Error("Invalid response from server.");
      }
      setPayLink(payload);
      setStatus("sent");
      toast.success("Thanks! We've let the organizer know you sent payment.");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Unable to confirm payment.",
      );
    } finally {
      setMarking(false);
    }
  };

  useEffect(() => {
    if (status !== "sent") {
      setShowFloatingInvite(false);
      return;
    }
    if (typeof window === "undefined") {
      return;
    }

    lastScrollYRef.current = window.scrollY;
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const nearTop = currentScrollY < 80;
      const nearBottom =
        document.documentElement.scrollHeight -
          (currentScrollY + window.innerHeight) <
        200;

      setShowFloatingInvite(!nearTop && !nearBottom);

      lastScrollYRef.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [status]);

  const showInstructions = !loading && !error && payLink;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-md mx-auto px-4 py-10 space-y-6">
        <header className="text-center space-y-2">
          <p className="text-sm uppercase tracking-wide text-muted-foreground">
            Payment Summary
          </p>
          <h1 className="text-2xl font-semibold">
            {payLink?.title || "Send a secure bank transfer"}
          </h1>
          <p className="text-sm text-muted-foreground">
            Review the payment details below.
          </p>
        </header>

        {loading && (
          <Card>
            <CardContent className="p-6 space-y-4 animate-pulse">
              <div className="h-4 bg-muted rounded" />
              <div className="h-4 bg-muted rounded" />
              <div className="h-24 bg-muted rounded" />
            </CardContent>
          </Card>
        )}

        {error && (
          <Card className="border-destructive/40 bg-destructive/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-5 w-5" />
                Link unavailable
              </CardTitle>
              <CardDescription>{error}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full" onClick={() => window.location.reload()}>
                Try Again
              </Button>
            </CardContent>
          </Card>
        )}

        {showInstructions && (
          <>
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <Avatar className="h-14 w-14">
                  {payLink?.owner?.avatar ? (
                    <AvatarImage
                      src={payLink.owner.avatar}
                      alt={recipientName}
                      className="object-cover"
                    />
                  ) : (
                    <AvatarFallback>
                      {recipientName
                        .split(" ")
                        .map((name) => name[0])
                        .slice(0, 2)
                        .join("")}
                    </AvatarFallback>
                  )}
                </Avatar>
                <div>
                  <p className="text-sm text-muted-foreground">Pay to</p>
                  <p className="text-lg font-semibold">{recipientName}</p>
                  {payLink?.expiresAt && (
                    <p className="text-xs text-muted-foreground">
                      Expires {new Date(payLink.expiresAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            <div
              className={`transition-all duration-300 rounded-2xl ${
                copyFeedback?.status === "success"
                  ? "ring-2 ring-primary/40 shadow-lg shadow-primary/20"
                  : ""
              } ${
                copyFeedback?.status === "error"
                  ? "ring-2 ring-destructive/40 shadow-lg shadow-destructive/20"
                  : ""
              }`}
            >
              <BankTransferInstructions
                amount={payLink.amount}
                currency={payLink.currency}
                region={region}
                recipientName={recipientName}
                paymentMethod={payLink.bankTransferInstructions}
                referenceDisplay={referenceDisplay}
                referenceDescription="Include this reference in your payment description"
                onCopyFeedback={triggerCopyFeedback}
                onMarkAsSent={status === "pending" ? handleMarkPaid : undefined}
                markAsSentLabel={
                  status === "pending" ? "Tap here after Transfer" : undefined
                }
                markAsSentDisabled={marking}
                status={status}
              />
            </div>
            {status === "sent" && (
              <>
                <Card className="border-success bg-success/10">
                  <CardContent className="flex items-start gap-3 p-4 text-success">
                    <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Bank Transfer</p>
                      <p className="text-sm text-muted-foreground">
                        We're waiting for confirmation from your bank. Don't worry - we'll let you know when it's done.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <div className="text-center">
                  <Button onClick={handleJoinClick} className={joinButtonClasses}>
                    Join {inviteeName} on Bankdrop
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </div>
      {showFloatingInvite && (
        <div className="fixed inset-x-0 bottom-6 px-4 sm:px-6 pointer-events-none flex justify-center">
          <Button
            onClick={handleJoinClick}
            className={`pointer-events-auto ${joinButtonClasses}`}
          >
            Join {inviteeName} on Bankdrop
          </Button>
        </div>
      )}
      <div className="fixed top-4 inset-x-0 px-4 sm:px-6 flex items-center justify-between z-30 pointer-events-none">
        <Button
          size="icon"
          className="pointer-events-auto h-11 w-11 sm:h-12 sm:w-12 rounded-full shadow-lg border border-white/20 bg-background/80 backdrop-blur text-primary"
          onClick={handleJoinClick}
          aria-label={`Open Bankdrop menu for ${inviteeName}`}
        >
          <BankdropGlyph />
        </Button>
        <Button
          size="icon"
          className="pointer-events-auto h-11 w-11 sm:h-12 sm:w-12 rounded-full shadow-lg border border-white/20 bg-background/80 backdrop-blur text-primary"
          onClick={handleSharePayLink}
          aria-label="Share this pay link"
        >
          <Share2 className="h-5 w-5" />
        </Button>
      </div>
      <Toaster position="top-center" richColors />
    </div>
  );
}
